<template>
  <div class="p-6 space-y-4">
    <header>
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
        Attach lists to this Location
      </h2>
      <p class="text-sm text-gray-500">
        Pick one or more lists. Selections will be appended to the applied set as active.
      </p>
      <p class="text-xs text-gray-400 mt-1">
        Tabs filter by scope:
        <span v-tooltip="'Admin-curated lists shared across all accounts.'" class="cursor-help underline decoration-dotted">Library</span>,
        <span v-tooltip="'Lists scoped to your account.'" class="cursor-help underline decoration-dotted">Account</span>,
        <span v-tooltip="'Lists scoped to this business.'" class="cursor-help underline decoration-dotted">Business</span>, or
        <span v-tooltip="'Lists scoped to this Location only.'" class="cursor-help underline decoration-dotted">This Location</span>.
      </p>
    </header>

    <div class="flex gap-2 border-b border-gray-200 dark:border-gray-700">
      <button
        v-for="tab in tabs"
        :key="tab.scope"
        type="button"
        v-tooltip="tab.tooltip"
        class="px-3 py-2 text-sm border-b-2"
        :class="activeTab === tab.scope ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'"
        @click="activeTab = tab.scope">
        {{ tab.label }} ({{ filteredByScope(tab.scope).length }})
      </button>
    </div>

    <input
      v-model="filterText"
      type="text"
      placeholder="Filter by list name…"
      class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white">

    <div v-if="loading" class="flex items-center gap-2 text-sm text-gray-500">
      <UiSpinner :size="4" screen-reader="Loading lists" />
      <span>Loading available lists…</span>
    </div>

    <div v-else-if="visibleLists.length === 0"
      class="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
      <i class="far fa-folder-open text-2xl text-gray-300 dark:text-gray-600 mb-2" />
      <p class="text-sm text-gray-500">
        No lists match.
      </p>
      <p v-if="filterText" class="text-xs text-gray-400">
        Try clearing the filter or switching tabs.
      </p>
    </div>

    <ul v-else class="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
      <li v-for="list in visibleLists" :key="list.id" class="py-2 flex items-center gap-3">
        <input type="checkbox" :checked="selectedIds.has(list.id)" @change="toggleSelect(list.id)">
        <div class="flex-1">
          <div class="text-sm font-medium text-gray-900 dark:text-white">
            {{ list.name }}
          </div>
          <div class="text-xs text-gray-500">
            <span v-tooltip="SCOPE_TOOLTIPS[list.scope] || ''" class="capitalize cursor-help">{{ list.scope }}</span>
            <span v-if="list.vertical"> · {{ list.vertical }}</span>
            <span v-if="list.purpose"> · {{ list.purpose }}</span>
            · {{ list.keyword_count }} keywords
          </div>
        </div>
      </li>
    </ul>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>

    <div class="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
      <button type="button" class="btn btn-secondary btn-sm" @click="$emit('on-close-panel')">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="selectedIds.size === 0"
        @click="confirm">
        Attach {{ selectedIds.size }} list{{ selectedIds.size === 1 ? '' : 's' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastError } from '~/javascript/utils/toast'

const SCOPE_TOOLTIPS = {
  admin: 'Library — admin-curated, read-only.',
  account: 'Account — your account, available across all locations.',
  business: 'Business — scoped to this business.',
  location: 'This Location — scoped to this Location only.'
}

const props = defineProps({
  locationId: { type: String, required: true },
  excludeIds: { type: Array, default: () => []}
})

const emit = defineEmits(['attach', 'on-close-panel'])

const tabs = [
  { scope: 'all', label: 'All', tooltip: 'Show lists from every scope.' },
  { scope: 'admin', label: 'Library', tooltip: 'Admin-curated, read-only lists.' },
  { scope: 'account', label: 'Account', tooltip: 'Lists scoped to your account.' },
  { scope: 'business', label: 'Business', tooltip: 'Lists scoped to this business.' },
  { scope: 'location', label: 'This Location', tooltip: 'Lists scoped to this Location only.' }
]

const availableLists = ref([])
const selectedIds = ref(new Set())
const filterText = ref('')
const activeTab = ref('all')
const loading = ref(false)
const error = ref(null)

const fetchAvailable = async() => {
  loading.value = true
  try {
    const res = await fetch(`/ajax/locations/${props.locationId}/negative_keyword_applied_set`, {
      headers: { Accept: 'application/json' }
    })
    if (!res.ok) throw new Error(`Load failed: ${res.status}`)
    const data = await res.json()
    availableLists.value = data.available_lists.filter((l) => !props.excludeIds.includes(l.id))
  } catch (e) {
    error.value = e.message
    toastError(e)
  } finally {
    loading.value = false
  }
}

const filteredByScope = (scope) => {
  if (scope === 'all') return availableLists.value
  return availableLists.value.filter((l) => l.scope === scope)
}

const visibleLists = computed(() => {
  let list = filteredByScope(activeTab.value)
  if (filterText.value) {
    const f = filterText.value.toLowerCase()
    list = list.filter((l) => l.name.toLowerCase().includes(f))
  }
  return list
})

const toggleSelect = (id) => {
  if (selectedIds.value.has(id)) selectedIds.value.delete(id)
  else selectedIds.value.add(id)
  selectedIds.value = new Set(selectedIds.value)
}

const confirm = () => {
  const selected = availableLists.value.filter((l) => selectedIds.value.has(l.id))
  emit('attach', selected)
  emit('on-close-panel')
}

onMounted(fetchAvailable)
</script>
